import React, { useEffect, useRef, useState } from "react";
import { message, Spin } from "antd";
import requests from "../../../axios/requests";
import Button from "../../../components/reuseComponents/Button/Button";
import variableLanguage from "../../../utils/hooks/hookGetVariablesLanguage";

const POLL_INTERVAL_MS = 4000;
const IN_PROGRESS_STATUSES = ["pending", "processing"];

// Реальные бэкенд-стадии (SCORM Engine create->preview->approve->build, затем сборка
// материала, затем -- если запрошен тест -- DeepSeek-генерация вопросов) не выставляют
// собственный статус наружу -- ScormExport.status знает только pending/processing/completed/
// failed. Вместо того чтобы городить новый API под one-off прогресс-бар, крутим правдоподобные
// фразы по локальному таймеру, независимо от реального поллинга -- пользователь видит, что
// процесс жив и куда-то движется, а не просто застывший спиннер (весь цикл занимает ~15-20с,
// поэтому 4 фразы по ~3.5с укладываются примерно в реальное время).
const PROGRESS_MESSAGE_INTERVAL_MS = 3500;
const PROGRESS_MESSAGES = [
  "Анализируем текст материала…",
  "Разбиваем материал на слайды…",
  "Собираем SCORM-пакет…",
  "Оформляем в выбранном шаблоне…",
];
const PROGRESS_MESSAGES_WITH_TEST = [
  ...PROGRESS_MESSAGES,
  "Генерируем вопросы теста…",
];

// Kept in sync with the backend's KNOWN_TEMPLATE_IDS whitelist
// (API_v2/web/teacher/stage/views.py) -- only "dark_it" and "corporate" are actually registered
// in the SCORM Engine today, the rest render as Dark IT until their real templates exist (see
// ADR-010). Still shown as selectable now on purpose: picking one is never destructive or
// wrong, it just doesn't look different yet.
// `nameVar` points at the matching translated theme name in constantsLanguages.js
// (courseManageViewLesson.variable_38..49) -- names follow the interface language (the
// "Сменить язык" toggle), same as every other label in this screen, not the course content
// language.
const THEME_META = [
  { id: "dark_it", accent: "#00d4ff", nameVar: "variable_38" },
  { id: "corporate", accent: "#1a56db", nameVar: "variable_39" },
  { id: "industrial", accent: "#ff6b1a", nameVar: "variable_40" },
  { id: "medical", accent: "#0077b6", nameVar: "variable_41" },
  { id: "education", accent: "#7c3aed", nameVar: "variable_42" },
  { id: "fintech", accent: "#f0c040", nameVar: "variable_43" },
  { id: "cybersecurity", accent: "#00ff41", nameVar: "variable_44" },
  { id: "legal", accent: "#8b6914", nameVar: "variable_45" },
  { id: "logistics", accent: "#0ea5e9", nameVar: "variable_46" },
  { id: "hr_soft", accent: "#e85d26", nameVar: "variable_47" },
  { id: "construction", accent: "#ffffff", nameVar: "variable_48" },
  { id: "retail", accent: "#e63946", nameVar: "variable_49" },
];

const downloadZip = (targetId, errorText) => {
  requests.stages
    .download_scorm_export(targetId)
    .then(({ data }) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `scorm-${targetId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    })
    .catch((err) => {
      console.error(err);
      message.error(errorText);
    });
};

// First iteration (2026-07-28 product decision, ported 1:1 from Staff): 3 question types,
// max 5 each, only text (no image generation), quiz always training mode. No i18n keys added
// for this specific block -- matches Staff's own convention, no i18n infra was set up for it
// there either.
const QUIZ_TYPE_META = [
  { key: "quiz_single_count", label: "Один или несколько вариантов" },
  { key: "quiz_matching_count", label: "Установление пар" },
  { key: "quiz_ordering_count", label: "Установление последовательности" },
];
const MAX_QUESTIONS_PER_TYPE = 5;

const ScormThemeGrid = ({ themes, selectedTheme, onSelect }) => (
  <div className="scorm_theme_grid">
    {themes.map((t) => (
      <button
        key={t.id}
        type="button"
        className="scorm_theme_card"
        aria-pressed={selectedTheme === t.id}
        style={{ "--card-accent": t.accent }}
        onClick={() => onSelect(t.id)}
      >
        <span className="scorm_theme_name">{t.name}</span>
        <span className={`scorm_theme_thumb thumb_${t.id}`}>
          <span className="scorm_theme_check">✓</span>
        </span>
      </button>
    ))}
  </div>
);

const ScormExport = (props) => {
  const { id, text, theory_type, generated_from_id, onMaterialCreated } = props;

  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("dark_it");
  const [includeTest, setIncludeTest] = useState(true);
  const [quizCounts, setQuizCounts] = useState({
    quiz_single_count: 3, quiz_ordering_count: 3, quiz_matching_count: 3,
  });
  const [progressStep, setProgressStep] = useState(0);
  const pollRef = useRef(null);
  const progressIntervalRef = useRef(null);
  // true = "nothing new to announce" -- covers the initial mount/page-reload case, where a
  // material may already be completed from an earlier session and must NOT re-notify. Only
  // handleGenerate() (a generation the user just started, first run or "Пересоздать") arms it.
  const notifiedRef = useRef(true);

  const textCreate = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_23" });
  const textRecreate = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_24" });
  const textPickerTitle = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_25" });
  const textTestLabel = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_26" });
  const textTestSub = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_27" });
  const textGenerate = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_28" });
  const textStarting = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_29" });
  const textCancel = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_30" });
  const textGenerating = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_31" });
  const textGenerationError = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_32" });
  const textRetry = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_33" });
  const textExportStartError = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_34" });
  const textCreatedSuccess = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_35" });
  const textDownloadError = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_36" });
  const textExportScorm = variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_37" });

  const themeNames = [
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_38" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_39" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_40" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_41" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_42" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_43" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_44" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_45" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_46" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_47" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_48" }),
    variableLanguage({ keyPage: "courseManageViewLesson", keyVariable: "variable_49" }),
  ];
  const themes = THEME_META.map((t, i) => ({ id: t.id, accent: t.accent, name: themeNames[i] }));

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const stopProgressMessages = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setProgressStep(0);
  };

  // Single source of truth: given a fetched status, either keep/start polling or stop it.
  // Called both from the mount-time fetch and from the interval tick itself, so a status that's
  // already in-progress when first observed (e.g. component remounted mid-generation, or a page
  // reload while an earlier run is still going) resumes polling instead of getting stuck showing
  // "Генерация запущена..." forever with no interval actually running.
  const syncPollingWithStatus = (status) => {
    if (IN_PROGRESS_STATUSES.includes(status)) {
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
      }
      if (!progressIntervalRef.current) {
        progressIntervalRef.current = setInterval(() => {
          setProgressStep((s) => s + 1);
        }, PROGRESS_MESSAGE_INTERVAL_MS);
      }
    } else {
      stopPolling();
      stopProgressMessages();
    }
  };

  const fetchStatus = () => {
    requests.stages
      .get_scorm_export(id)
      .then((res) => {
        setExportData(res.data);
        // Remember the last-used theme/test choice as the default for next time (e.g.
        // "Пересоздать SCORM" after tweaking the article) instead of always resetting to Dark IT.
        if (res.data?.template_id) setSelectedTheme(res.data.template_id);
        if (typeof res.data?.include_test === "boolean") setIncludeTest(res.data.include_test);
        setQuizCounts((prev) => ({
          quiz_single_count: res.data?.quiz_single_count ?? prev.quiz_single_count,
          quiz_ordering_count: res.data?.quiz_ordering_count ?? prev.quiz_ordering_count,
          quiz_matching_count: res.data?.quiz_matching_count ?? prev.quiz_matching_count,
        }));

        const status = res.data?.status;
        const scormReady = Boolean(res.data?.created_material_id);
        const testRequested = Boolean(res.data?.include_test);
        const quizReady = Boolean(res.data?.created_quiz_material_id);
        // Бэкенд помечает экспорт "completed" и только ПОСЛЕ ЭТОГО создаёт sibling-материал(ы)
        // отдельными сохранениями (сначала SCORM, затем -- если запрошен тест -- ещё и тест,
        // генерация вопросов через DeepSeek занимает заметное время) -- считаем всё ещё
        // "в процессе" для целей поллинга, пока не готовы ВСЕ ожидаемые материалы, иначе
        // список обновится (onMaterialCreated) раньше, чем тест реально появится в БД, и
        // тест придётся ловить только ручным обновлением страницы.
        const allReady = scormReady && (!testRequested || quizReady);
        syncPollingWithStatus(status === "completed" && !allReady ? "processing" : status);

        if (status === "completed" && allReady && !notifiedRef.current) {
          notifiedRef.current = true;
          message.success(testRequested ? "SCORM-материал и тест созданы" : textCreatedSuccess);
          onMaterialCreated?.([res.data?.created_material_id, res.data?.created_quiz_material_id].filter(Boolean));
        }
      })
      .catch((err) => {
        console.error(err);
        stopPolling();
      });
  };

  useEffect(() => {
    // Viewing the generated SCORM material itself (see the theory_type === "LMS" branch below) --
    // there is no export job of its OWN to poll (the export lives on the source article), so skip
    // the polling loop entirely for that branch.
    if (theory_type === "LMS") return undefined;
    fetchStatus();
    return () => {
      stopPolling();
      stopProgressMessages();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, theory_type]);

  const handleGenerate = () => {
    setLoading(true);
    notifiedRef.current = false; // arm: the next observed "completed" belongs to this run
    requests.stages
      .create_scorm_export(id, { templateId: selectedTheme, includeTest, ...quizCounts })
      .then((res) => {
        setExportData(res.data);
        setPickerOpen(false);
        syncPollingWithStatus(res.data?.status);
      })
      .catch((err) => {
        console.error(err);
        message.error(textExportStartError);
      })
      .finally(() => setLoading(false));
  };

  const handleRetryAfterFailure = () => {
    setLoading(true);
    notifiedRef.current = false;
    requests.stages
      .create_scorm_export(id, { templateId: selectedTheme, includeTest, ...quizCounts })
      .then((res) => {
        setExportData(res.data);
        syncPollingWithStatus(res.data?.status);
      })
      .catch((err) => {
        console.error(err);
        message.error(textExportStartError);
      })
      .finally(() => setLoading(false));
  };

  // Viewing the generated SCORM sibling material: same slot at the top of the material view as
  // the source article's "Создать SCORM", same button style, but points at the SOURCE article's
  // export (generated_from_id) since that is what owns the downloadable artifact -- reuses the
  // exact same existing download endpoint, nothing new on the backend beyond exposing this one
  // already-existing id.
  if (theory_type === "LMS") {
    if (!generated_from_id) return null; // manually-uploaded LMS package -- no export lineage to offer
    return (
      <div className="scorm_export_block">
        <Button
          text={textExportScorm}
          click={() => downloadZip(generated_from_id, textDownloadError)}
          width="auto"
        />
      </div>
    );
  }

  if (theory_type === "V" || theory_type === "Q") return null;
  if (!text || !text.trim()) return null;

  const status = exportData?.status;

  // Always-visible side panel (matches the approved design): the theme grid + test toggle live
  // permanently to the right of the article body for an Article-type material, not behind a
  // click -- only the primary action button's label/behavior changes with export state.
  return (
    <div className="scorm_export_block">
      <div className="scorm_theme_picker">
        <div className="scorm_theme_picker-title">{textPickerTitle}</div>
        <ScormThemeGrid themes={themes} selectedTheme={selectedTheme} onSelect={setSelectedTheme} />

        <div className="scorm_test_row">
          <div>
            <div className="scorm_test_row-label">{textTestLabel}</div>
            <div className="scorm_test_row-sub">{textTestSub}</div>
          </div>
          <button
            type="button"
            className="scorm_switch"
            role="switch"
            aria-checked={includeTest}
            data-on={includeTest}
            onClick={() => setIncludeTest((v) => !v)}
          />
        </div>

        {includeTest && (
          <div className="scorm_quiz_counts">
            {QUIZ_TYPE_META.map((t) => (
              <div className="scorm_quiz_count_row" key={t.key}>
                <span className="scorm_quiz_count_label">{t.label}</span>
                <input
                  type="number"
                  className="scorm_quiz_count_input"
                  min={0}
                  max={MAX_QUESTIONS_PER_TYPE}
                  value={quizCounts[t.key]}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const clamped = Number.isFinite(raw)
                      ? Math.max(0, Math.min(MAX_QUESTIONS_PER_TYPE, raw))
                      : 0;
                    setQuizCounts((prev) => ({ ...prev, [t.key]: clamped }));
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <div className="scorm_picker_actions">
          {!status && (
            <Button
              text={loading ? textStarting : textCreate}
              click={handleGenerate}
              disabled={loading}
              width="auto"
            />
          )}

          {status === "completed" && (
            // Regenerates the existing sibling material in place (create_scorm_export is idempotent
            // on ScormExport.created_material -- see Stage B), not a fresh download: the user's flow
            // here is "improve the article, then refresh its SCORM" rather than "grab today's zip".
            // Downloading the built package is still available from inside the SCORM material itself
            // via the "Экспортировать SCORM" button below (theory_type === "LMS" branch).
            <Button
              text={loading ? textStarting : textRecreate}
              click={handleGenerate}
              disabled={loading}
              width="auto"
            />
          )}

          {status === "failed" && (
            <Button text={textRetry} click={handleRetryAfterFailure} disabled={loading} width="auto" />
          )}
        </div>

        {IN_PROGRESS_STATUSES.includes(status) && (
          <div className="scorm_export_status">
            <Spin size="small" />
            {(includeTest ? PROGRESS_MESSAGES_WITH_TEST : PROGRESS_MESSAGES)[
              Math.min(progressStep, (includeTest ? PROGRESS_MESSAGES_WITH_TEST : PROGRESS_MESSAGES).length - 1)
            ]}
          </div>
        )}

        {status === "failed" && (
          <div className="scorm_export_error">{exportData?.error_message || textGenerationError}</div>
        )}
      </div>
    </div>
  );
};

export default ScormExport;
